import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO, { SITE_URL } from "@/components/SEO";
import { markPrerenderReady } from "@/lib/seo";
import {
  getPublishedPosts,
  getCategories,
  readingMinutes,
  formatDate,
} from "@/blog/posts";

/* Shared by the list page and the "Keep reading" row on article pages. */
export function PostCard({ post, featured = false }) {
  const to = `/blog/${post.slug}`;

  if (featured) {
    return (
      <Link
        to={to}
        data-testid="blog-featured-card"
        className="group block overflow-hidden rounded-[2rem] border border-ink/10 bg-white transition-shadow duration-300 hover:shadow-[0_30px_70px_-40px_rgba(18,16,14,0.5)]"
      >
        <div className="overflow-hidden bg-cream">
          <img
            src={post.cover.src}
            alt={post.cover.alt}
            width="1200"
            height="630"
            className="aspect-[1200/630] w-full object-cover transition-transform duration-500 group-hover:scale-[1.015]"
          />
        </div>
        <div className="grid gap-6 p-7 md:grid-cols-[1.3fr_1fr] md:gap-12 md:p-10">
          <div>
            <span className="w-fit rounded-full bg-lilac px-3 py-1 text-xs font-bold text-ink">
              {post.category}
            </span>
            <h2 className="mt-4 font-display text-3xl leading-[1.05] tracking-tight text-ink md:text-4xl">
              {post.title}
            </h2>
          </div>
          <div className="flex flex-col justify-between gap-5">
            <p className="text-[16px] leading-relaxed text-ink/70">{post.excerpt}</p>
            <div className="flex items-center gap-4 text-sm text-ink/50">
              <span>{formatDate(post.date)}</span>
              <span>{readingMinutes(post)} min read</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      data-testid="blog-card"
      className="group flex flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white transition-shadow duration-300 hover:shadow-[0_24px_60px_-36px_rgba(18,16,14,0.5)]"
    >
      <div className="overflow-hidden bg-cream">
        <img
          src={post.cover.src}
          alt={post.cover.alt}
          width="1200"
          height="630"
          loading="lazy"
          className="aspect-[1200/630] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <span className="w-fit rounded-full bg-lilac px-3 py-1 text-xs font-bold text-ink">
          {post.category}
        </span>
        <h3 className="mt-3 font-display text-xl leading-snug tracking-tight text-ink">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-3 text-[15px] leading-relaxed text-ink/65">{post.excerpt}</p>
        <div className="mt-auto flex items-center gap-4 pt-5 text-[13px] text-ink/50">
          <span>{formatDate(post.date)}</span>
          <span>{readingMinutes(post)} min read</span>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const posts = getPublishedPosts();
  const categories = getCategories();
  const [category, setCategory] = useState("All");

  useEffect(() => {
    markPrerenderReady();
  }, []);

  const filtered = useMemo(
    () => (category === "All" ? posts : posts.filter((p) => p.category === category)),
    [posts, category]
  );
  const [featured, ...rest] = filtered;

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Checkout DM Blog",
    url: `${SITE_URL}/blog`,
    description:
      "Practical guides on selling on Instagram, DM checkout and automation for small brands.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: SITE_URL },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE_URL}/blog/${p.slug}`,
      datePublished: p.date,
    })),
  };

  return (
    <>
      <SEO
        title="Blog | Checkout DM: Instagram Selling, DM Checkout & Automation Guides"
        description="Practical guides on selling on Instagram, DM checkout, payments and automation for small brands and creators."
        path="/blog"
        structuredData={blogSchema}
      />
      <Navbar />

      <section className="px-6 pb-8 pt-16 md:px-12 md:pt-24">
        <div className="mx-auto max-w-5xl">
          <h1 className="max-w-3xl font-display text-4xl leading-[0.98] tracking-tighter md:text-6xl">
            Practical guides for selling on Instagram.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-ink/70">
            How small brands and creators turn DMs into orders: checkout, payments, automation and
            the small details that decide whether a customer buys.
          </p>

          {categories.length > 1 && (
            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter by category">
              {["All", ...categories].map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={category === c}
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    category === c
                      ? "bg-ink text-cream"
                      : "bg-white text-ink/70 ring-1 ring-ink/10 hover:text-ink"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px-6 pb-24 pt-4 md:px-12">
        <div className="mx-auto max-w-5xl">
          {featured ? (
            <PostCard post={featured} featured />
          ) : (
            <p className="rounded-3xl border border-ink/10 bg-white p-8 text-ink/60">
              No articles in this category yet.
            </p>
          )}

          {rest.length > 0 && (
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          )}

          {posts.length === 1 && (
            <p className="mt-10 text-center text-sm text-ink/50">
              More articles are on the way. In the meantime, the{" "}
              <Link to="/help" className="font-semibold text-hotpink hover:underline">
                Help Guide
              </Link>{" "}
              covers how everything in the dashboard works.
            </p>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}
